/* ─────────────────────────────────────────────────────────────
   EARNINGS REVIEW TAB
   GET /api/v1/stocks/{ticker}/earnings-review

   Replicates the reference earnings note (MU_Q3FY26_Earnings_Note) section by
   section, in the note's own order:

     masthead + BEAT/MISS/MIXED badge   ← latest_quarter, price_at_review
     Headline                            ← headline (carries the qualified-beat
                                            GAAP-vs-operational sentence)
     Variance table                      ← variance_table (+ data_checks flags and
                                            flag: "non_operating_gain" markers)
     Data checks                         ← data_checks
     GAAP-vs-operational flag box        ← eps_normalization
     Key drivers vs thesis               ← key_drivers[]
     Estimate & model implications       ← estimate_model_implications[]
     Watch items / risks                 ← watch_items[]
     Management sentiment                ← sentiment (score card, sub-score bars,
                                            0–100 band strip built from
                                            band_scale, trend, evidence table)
     Guidance                            ← guidance_vs_actual + sentiment.guidance
                                            (incl. full-year margin, opex and the
                                            excluded-from-outlook row)
     Beat/miss history                   ← quarters[]
     Next report                         ← next_earnings

   Every section of the reference note is now served. The prose sections come
   from the stored per-quarter note and are null until its seeder has run for
   the stock, so render-what's-present still applies.

   Null rule: any field may be null / any list empty. Render what's present,
   hide what's null, never show "N/A".
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { Tooltip as MuiTooltip } from "@mui/material";
import { getEarningsReview, getEarningsSentimentHistory } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";
import {
  resolveBand,
  SENTIMENT_DISCLAIMER,
  fiscalLabel,
  reportTimeLabel,
  fmtDate,
  fmtPct,
  fmtNum,
  scoreBarWidth,
  subScoreColor,
  badgeColor,
  varianceKind,
  dataChecksByMetric,
  NOT_RELIABLE,
  bandStripSegments,
  epsNormStatusNote,
  isNonOperatingGainRow,
} from "../../../config/earningsNote";
import {
  NotePage, SectionTitle, Card, TileGrid, Banner, Pill,
  Scroller, Table, Th, Td, Tl, NoteSkeleton, NoteEmpty, toneColor,
  NoteList,
} from "../Widgets/earningsNoteUI";

/* Beat / miss tone from a surprise-percent sign. */
const surpriseTone = (pct) => {
  const n = Number(pct);
  if (pct == null || Number.isNaN(n)) return "muted";
  return n >= 0 ? "pos" : "neg";
};

/* ── 0–100 band strip — cutoffs come from sentiment.band_scale, not hardcoded ── */
const BandStrip = ({ score, bandScale }) => {
  const pos = Math.max(0, Math.min(100, Number(score) || 0));
  const segments = bandStripSegments(bandScale);
  return (
    <div style={{ marginTop: "var(--spacing-md)" }}>
      <div
        style={{
          color: "var(--color-text-muted)",
          fontSize: "var(--font-size-xs)",
          fontWeight: 500,
          marginBottom: 6,
        }}
      >
        Score bands — where the 0–100 composite lands
      </div>

      <div style={{ position: "relative", paddingTop: 24 }}>
        <div
          style={{
            position: "absolute",
            top: 0,
            left: `${pos}%`,
            transform: "translateX(-50%)",
            textAlign: "center",
            whiteSpace: "nowrap",
          }}
        >
          <span
            style={{
              backgroundColor: "var(--color-text-primary)",
              /* --color-surface is the true inverse of --color-text-primary in
                 both themes; --color-bg-primary is near-transparent in light. */
              color: "var(--color-surface, #FFFFFF)",
              borderRadius: "var(--radius-sm)",
              padding: "2px 6px",
              fontSize: "var(--font-size-xs)",
              fontWeight: "var(--font-weight-bold)",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {fmtNum(score, 1)}
          </span>
          <div style={{ color: "var(--color-text-primary)", fontSize: 11, lineHeight: 1 }}>▼</div>
        </div>

        <div
          style={{
            display: "flex",
            height: 24,
            borderRadius: "var(--radius-md)",
            overflow: "hidden",
            color: "#FFFFFF",
            fontSize: 10,
            fontWeight: "var(--font-weight-semibold)",
            lineHeight: "24px",
            textAlign: "center",
          }}
        >
          {segments.map((b) => (
            <div key={b.key} style={{ width: `${b.width}%`, backgroundColor: b.color }}>
              {b.min}–{b.max}
            </div>
          ))}
        </div>

        <div
          style={{
            display: "flex",
            marginTop: 4,
            color: "var(--color-text-muted)",
            fontSize: 9.5,
            fontWeight: "var(--font-weight-semibold)",
            textAlign: "center",
            lineHeight: 1.3,
          }}
        >
          {segments.map((b) => (
            <div key={b.key} style={{ width: `${b.width}%`, padding: "0 2px" }}>
              {b.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Sub-score bars — one row per rubric dimension ── */
const DimensionBars = ({ dimensions }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "minmax(150px, 215px) 1fr 40px",
      gap: "7px 12px",
      alignItems: "center",
      fontSize: "var(--font-size-sm)",
    }}
  >
    {dimensions.map((d) => {
      const tip = [
        d.weight != null ? `Rubric weight ${Math.round(Number(d.weight) * 100)}%` : null,
        d.prepared != null ? `Prepared remarks ${fmtNum(d.prepared, 1)}/10` : null,
        d.qa != null ? `Q&A ${fmtNum(d.qa, 1)}/10` : null,
        d.quote ? `“${d.quote}”` : null,
      ]
        .filter(Boolean)
        .join(" · ");

      return (
        <React.Fragment key={d.key ?? d.label}>
          <MuiTooltip title={tip} placement="top" arrow enterDelay={300}>
            <span
              style={{
                color: "var(--color-text-secondary)",
                fontWeight: "var(--font-weight-medium)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                cursor: tip ? "help" : "default",
              }}
            >
              {d.label ?? d.key}
              {d.weight != null && (
                <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}>
                  {" "}
                  ({Math.round(Number(d.weight) * 100)}%)
                </span>
              )}
            </span>
          </MuiTooltip>

          <div
            style={{
              backgroundColor: "var(--color-bg-secondary)",
              border: "1px solid var(--color-border-primary)",
              borderRadius: 5,
              height: 10,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${scoreBarWidth(d.score)}%`,
                backgroundColor: subScoreColor(d.score),
                height: "100%",
                borderRadius: 5,
                transition: "width var(--transition-normal)",
              }}
            />
          </div>

          <span
            style={{
              color: "var(--color-text-primary)",
              fontWeight: "var(--font-weight-bold)",
              fontVariantNumeric: "tabular-nums",
              textAlign: "right",
            }}
          >
            {fmtNum(d.score, 1)}
          </span>
        </React.Fragment>
      );
    })}
  </div>
);

/* ── Variance-table cell formatting ── */
const varianceValue = (metric, v) => {
  if (v == null) return "—";
  const kind = varianceKind(metric);
  if (kind === "pct") return fmtPct(v, { signed: false, decimals: 1 });
  if (kind === "perShare") return formatCurrency(v);
  return formatCurrency(v, { compact: true, decimals: 2 });
};

/* Prior-period cell: the level, with its change in brackets — as the note shows it. */
const priorCell = (metric, value, pct) => {
  if (value == null && pct == null) return "—";
  const level = value != null ? varianceValue(metric, value) : "—";
  if (pct == null) return level;
  return `${level} (${fmtPct(pct, { decimals: 1 })})`;
};

/* ══════════════════════════════════════════════════════════════ */
/* ── One quarter's reasons: sub-scores, change vs the prior call, the quote behind
      each score, and the questions management avoided (history API, 2026-10-09). ── */
const signed1 = (v) => (v == null ? null : `${v > 0 ? "+" : v < 0 ? "−" : "±"}${Math.abs(Number(v)).toFixed(1)}`);

const QuarterDetail = ({ q, prior }) => {
  if (!q) return null;
  const qb = resolveBand(q.band, q.composite_score);
  const compDelta =
    q.composite_score != null && prior?.composite_score != null
      ? Number(q.composite_score) - Number(prior.composite_score)
      : null;
  const priorLabel = prior ? fiscalLabel(prior.fiscal_year, prior.fiscal_quarter) : null;
  const dodges = (q.dodges ?? []).filter(Boolean);
  return (
    <Card accent={qb?.color} style={{ marginTop: "var(--spacing-sm)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "var(--spacing-sm)" }}>
        <span style={{ fontWeight: "var(--font-weight-bold)", color: "var(--color-text-primary)" }}>
          {fiscalLabel(q.fiscal_year, q.fiscal_quarter)}
        </span>
        <span style={{ color: qb?.color, fontWeight: "var(--font-weight-bold)", fontVariantNumeric: "tabular-nums" }}>
          {fmtNum(q.composite_score, 1)} · {q.band}
        </span>
        {compDelta != null && (
          <span style={{ color: compDelta >= 0 ? "var(--color-success)" : "var(--color-error)", fontSize: "var(--font-size-sm)" }}>
            {signed1(compDelta)} vs {priorLabel}
          </span>
        )}
        {q.transcript_date && (
          <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}>call {fmtDate(q.transcript_date)}</span>
        )}
      </div>
      {q.comparable_with_prior === false && (
        <div style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)", marginTop: 4 }}>
          Scored under a different rubric version than {priorLabel} — read the changes loosely.
        </div>
      )}
      {q.summary && (
        <div style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)", lineHeight: 1.6, marginTop: "var(--spacing-sm)" }}>
          {q.summary}
        </div>
      )}
      <Scroller>
        <Table>
          <thead>
            <tr>
              <Th left>Dimension</Th>
              <Th>Score</Th>
              <Th>{priorLabel ? `vs ${priorLabel}` : "Change"}</Th>
              <Th>Prepared</Th>
              <Th>Q&A</Th>
              <Th left>Why — the words on the call</Th>
            </tr>
          </thead>
          <tbody>
            {(q.dimensions ?? []).map((d) => (
              <tr key={d.key}>
                <Tl>
                  {d.label ?? d.key}
                  {d.weight != null ? (
                    <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}> ({Math.round(Number(d.weight) * 100)}%)</span>
                  ) : null}
                </Tl>
                <Td bold style={{ color: d.score != null ? subScoreColor(d.score) : undefined }}>
                  {d.score != null ? fmtNum(d.score, 1) : "—"}
                </Td>
                <Td style={{ color: d.delta == null ? "var(--color-text-muted)" : d.delta >= 0 ? "var(--color-success)" : "var(--color-error)" }}>
                  {d.delta != null ? signed1(d.delta) : "—"}
                </Td>
                <Td tone="muted">{d.prepared != null ? fmtNum(d.prepared, 1) : "—"}</Td>
                <Td tone="muted">{d.qa != null ? fmtNum(d.qa, 1) : "—"}</Td>
                <Td left wrap>
                  {d.quote ? (
                    <span style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)" }}>
                      “{d.quote}”
                      {d.verified ? (
                        <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}> · verified</span>
                      ) : null}
                    </span>
                  ) : (
                    <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)" }}>not discussed on the call</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Scroller>
      {dodges.length > 0 && (
        <div style={{ marginTop: "var(--spacing-sm)", fontSize: "var(--font-size-sm)", color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
          <strong style={{ color: "var(--color-warning)" }}>Questions management avoided: </strong>
          {dodges.join(" · ")}
        </div>
      )}
    </Card>
  );
};

const EarningsReview = ({ stock }) => {
  const ticker = stock?.ticker;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ error: false, notFound: false });
  const [qHist, setQHist] = useState([]);          // per-quarter reasons (history API); [] until/unless served
  const [selQ, setSelQ] = useState(null);           // "FY-FQ" of the open trend card

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setState({ error: false, notFound: false });
      const res = await getEarningsReview(ticker);
      if (cancelled) return;
      if (res?.success) setData(res.data);
      else setState({ error: !res?.notFound, notFound: !!res?.notFound });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  /* Per-quarter sentiment reasons — optional: a 404 (route not mounted yet) leaves
     the trend on sentiment_history, scores only. */
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setQHist([]);
    setSelQ(null);
    (async () => {
      const res = await getEarningsSentimentHistory(ticker, 8);
      if (!cancelled && res?.success && Array.isArray(res.data?.quarters)) setQHist(res.data.quarters);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker]);

  if (loading) return <NoteSkeleton blocks={[86, 200, 300]} />;

  if (state.notFound) return <NoteEmpty>{ticker} is not in coverage.</NoteEmpty>;
  if (state.error || !data)
    return <NoteEmpty>Earnings review data is not available for {ticker}.</NoteEmpty>;

  const {
    company_name,
    sector_name,
    industry_name,
    latest_quarter,
    headline,
    price_at_review,
    variance_table = [],
    data_checks = [],
    guidance_vs_actual,
    eps_normalization,
    key_drivers = [],
    estimate_model_implications = [],
    watch_items = [],
    next_earnings,
    quarters = [],
    sentiment,
    sentiment_history = [],
  } = data;

  const band = sentiment ? resolveBand(sentiment.band, sentiment.composite_score) : null;
  const dimensions = sentiment?.dimensions ?? [];
  const withQuotes = dimensions.filter((d) => d.quote);
  const guidance = sentiment?.guidance ?? null;
  const checks = dataChecksByMetric(data_checks);

  /* ── Guidance rows: guided figure + how it compares with the quarter ── */
  const guidanceRows = [];
  if (guidance?.revenue != null || guidance_vs_actual?.revenue_guided != null) {
    const guided = guidance_vs_actual?.revenue_guided ?? guidance?.revenue;
    guidanceRows.push({
      label: "Revenue",
      guided: formatCurrency(guided, { compact: true }),
      range:
        guidance?.revenue_low != null && guidance?.revenue_high != null
          ? `${formatCurrency(guidance.revenue_low, { compact: true })} – ${formatCurrency(
              guidance.revenue_high,
              { compact: true }
            )}`
          : null,
      vsActual:
        guidance_vs_actual?.revenue_vs_actual_pct != null
          ? fmtPct(guidance_vs_actual.revenue_vs_actual_pct, { decimals: 1 })
          : null,
      vsTone: surpriseTone(guidance_vs_actual?.revenue_vs_actual_pct),
    });
  }
  if (guidance?.gross_margin_pct != null || guidance_vs_actual?.gross_margin_guided_pct != null) {
    const g = guidance_vs_actual?.gross_margin_guided_pct ?? guidance?.gross_margin_pct;
    guidanceRows.push({
      label: "Gross margin",
      guided: fmtPct(g, { signed: false, decimals: 1 }),
      range: null,
      vsActual:
        guidance_vs_actual?.gross_margin_change_pts != null
          ? `${Number(guidance_vs_actual.gross_margin_change_pts) >= 0 ? "+" : "−"}${fmtNum(
              Math.abs(Number(guidance_vs_actual.gross_margin_change_pts)),
              1
            )} pts`
          : null,
      vsTone: surpriseTone(guidance_vs_actual?.gross_margin_change_pts),
    });
  }
  /* Full-year margin is management's VERBATIM phrase when the call gave a
     qualitative outlook (e.g. "mid seventies") — render it as given. */
  if (guidance?.gross_margin_full_year != null) {
    guidanceRows.push({
      label: "Gross margin (full year)",
      guided:
        typeof guidance.gross_margin_full_year === "number"
          ? fmtPct(guidance.gross_margin_full_year, { signed: false, decimals: 1 })
          : String(guidance.gross_margin_full_year),
      range: null,
      vsActual: null,
      vsTone: "muted",
      verbatim: typeof guidance.gross_margin_full_year !== "number",
    });
  }
  if (guidance?.opex != null) {
    guidanceRows.push({
      label: "Operating expenses",
      guided: formatCurrency(guidance.opex, { compact: true }),
      range: null,
      vsActual: null,
      vsTone: "muted",
    });
  }
  if (guidance?.opex_non_gaap != null) {
    guidanceRows.push({
      label: "Operating expenses (non-GAAP)",
      guided: formatCurrency(guidance.opex_non_gaap, { compact: true }),
      range: null,
      vsActual: null,
      vsTone: "muted",
    });
  }
  if (guidance?.eps != null || guidance_vs_actual?.eps_guided != null) {
    guidanceRows.push({
      label: "Diluted EPS (GAAP)",
      guided: formatCurrency(guidance_vs_actual?.eps_guided ?? guidance?.eps),
      range: null,
      vsActual:
        guidance_vs_actual?.eps_vs_actual_pct != null
          ? fmtPct(guidance_vs_actual.eps_vs_actual_pct, { decimals: 1 })
          : null,
      vsTone: surpriseTone(guidance_vs_actual?.eps_vs_actual_pct),
    });
  }
  if (guidance?.eps_non_gaap != null || guidance_vs_actual?.eps_non_gaap_guided != null) {
    guidanceRows.push({
      label: "Diluted EPS (non-GAAP)",
      guided: formatCurrency(guidance_vs_actual?.eps_non_gaap_guided ?? guidance?.eps_non_gaap),
      range: null,
      vsActual: null,
      vsTone: "muted",
    });
  }
  /* What management explicitly left OUT of the outlook — the note's flagged
     "excluded ⚠ / not in outlook" row. */
  if (guidance?.exclusions) {
    guidanceRows.push({
      label: "Not in the outlook",
      guided: String(guidance.exclusions),
      range: null,
      vsActual: null,
      vsTone: "muted",
      excluded: true,
      verbatim: true,
    });
  }

  const guidancePeriod = guidance_vs_actual?.period ?? guidance?.period ?? null;

  /* ── Next-report tiles ── */
  const nextTiles = next_earnings
    ? [
        { label: "Report date", value: fmtDate(next_earnings.report_date) },
        next_earnings.report_time
          ? { label: "Timing", value: reportTimeLabel(next_earnings.report_time) }
          : null,
        next_earnings.fiscal_quarter != null || next_earnings.fiscal_year != null
          ? {
              label: "Period",
              value: fiscalLabel(next_earnings.fiscal_year, next_earnings.fiscal_quarter),
            }
          : null,
        next_earnings.eps_estimate != null
          ? { label: "EPS estimate", value: formatCurrency(next_earnings.eps_estimate) }
          : null,
        next_earnings.revenue_estimate != null
          ? {
              label: "Revenue estimate",
              value: formatCurrency(next_earnings.revenue_estimate, { compact: true }),
            }
          : null,
      ].filter(Boolean)
    : [];

  const qoq = sentiment?.qoq_delta;
  const qoqTone = qoq == null ? "muted" : Number(qoq) >= 0 ? "pos" : "neg";

  /* ── Sentiment provenance — the sentiment's own "data as of" ── */
  const provenance = sentiment
    ? [
        sentiment.model ? `scored by ${sentiment.model}` : null,
        sentiment.scored_at ? `on ${fmtDate(sentiment.scored_at)}` : null,
        sentiment.blend?.prepared_weight != null && sentiment.blend?.qa_weight != null
          ? `blend ${Math.round(sentiment.blend.prepared_weight * 100)}% prepared / ${Math.round(
              sentiment.blend.qa_weight * 100
            )}% Q&A`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  const nothing =
    !headline && variance_table.length === 0 && !sentiment && quarters.length === 0 && nextTiles.length === 0;
  if (nothing)
    return <NoteEmpty>No earnings-review data is available for {ticker}.</NoteEmpty>;

  return (
    <NotePage>
      {/* ── Masthead ── */}
      <div
        style={{
          paddingTop: "var(--spacing-sm)",
          paddingBottom: "var(--spacing-sm)",
          borderBottom: "3px double var(--color-border-secondary)",
        }}
      >
        <div
          style={{
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-xs)",
            fontWeight: "var(--font-weight-semibold)",
            textTransform: "uppercase",
            letterSpacing: "0.14em",
          }}
        >
          {["Post-earnings review", sector_name, industry_name].filter(Boolean).join(" · ")}
        </div>

        <div
          style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}
        >
          <span
            style={{
              color: "var(--color-text-primary)",
              fontSize: "var(--font-size-xl)",
              fontWeight: "var(--font-weight-bold)",
            }}
          >
            {company_name || stock?.company_name} ({data.ticker || ticker})
            {latest_quarter && (
              <> — {fiscalLabel(latest_quarter.fiscal_year, latest_quarter.fiscal_quarter)}</>
            )}
          </span>

          {latest_quarter?.badge && (
            <span
              style={{
                backgroundColor: badgeColor(latest_quarter.badge),
                color: "#FFFFFF",
                borderRadius: "var(--radius-sm)",
                padding: "4px 10px",
                fontSize: "var(--font-size-xs)",
                fontWeight: "var(--font-weight-bold)",
                letterSpacing: "0.05em",
              }}
            >
              {latest_quarter.badge}
            </span>
          )}
        </div>

        <div
          style={{
            marginTop: 4,
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-xs)",
            lineHeight: 1.6,
          }}
        >
          {[
            latest_quarter?.period_end_date
              ? `Quarter ended ${fmtDate(latest_quarter.period_end_date)}`
              : null,
            sentiment?.transcript_date ? `call ${fmtDate(sentiment.transcript_date)}` : null,
            price_at_review?.price != null
              ? `price at review ${formatCurrency(price_at_review.price)}${
                  price_at_review.as_of ? ` (${fmtDate(price_at_review.as_of)})` : ""
                }`
              : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>

      {/* ── Headline ── */}
      {headline && (
        <p
          style={{
            color: "var(--color-text-primary)",
            fontSize: "var(--font-size-base)",
            lineHeight: 1.7,
            marginTop: "var(--spacing-md)",
          }}
        >
          <strong>Headline. </strong>
          {headline}
        </p>
      )}

      {/* ── Variance table ── */}
      {variance_table.length > 0 && (
        <>
          <SectionTitle caption="actual vs. consensus vs. prior periods">
            Variance table
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Metric</Th>
                  <Th>Actual</Th>
                  <Th>Consensus</Th>
                  <Th>Surprise</Th>
                  <Th>Prior year (YoY)</Th>
                  <Th>Prior quarter (QoQ)</Th>
                </tr>
              </thead>
              <tbody>
                {variance_table.map((r, i) => {
                  const unreliable = r.reliable === false;
                  const check = checks[r.metric];
                  const nonOp = isNonOperatingGainRow(r);
                  const rowTint =
                    unreliable || check || nonOp
                      ? { backgroundColor: "rgba(217,119,6,0.08)" }
                      : undefined;
                  return (
                    <tr key={r.metric ?? i}>
                      <Tl style={rowTint}>
                        {r.label ?? r.metric}
                        {(check || nonOp) && (
                          <MuiTooltip
                            title={
                              check?.note ??
                              "Flagged as a non-operating gain — see the GAAP-vs-operational box below."
                            }
                            placement="top"
                            arrow
                          >
                            <span style={{ color: "var(--color-warning)", cursor: "help" }}> ⚠</span>
                          </MuiTooltip>
                        )}
                      </Tl>
                      <Td bold style={rowTint}>
                        {unreliable ? NOT_RELIABLE : varianceValue(r.metric, r.actual)}
                      </Td>
                      <Td style={rowTint}>
                        {unreliable ? NOT_RELIABLE : varianceValue(r.metric, r.consensus)}
                      </Td>
                      <Td bold tone={surpriseTone(r.surprise_pct)} style={rowTint}>
                        {r.surprise_pct == null ? "—" : fmtPct(r.surprise_pct, { decimals: 1 })}
                      </Td>
                      <Td tone="muted" style={rowTint}>
                        {priorCell(r.metric, r.yoy, r.yoy_pct)}
                      </Td>
                      <Td tone="muted" style={rowTint}>
                        {priorCell(r.metric, r.qoq, r.qoq_pct)}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
          <div
            style={{
              marginTop: 6,
              color: "var(--color-text-muted)",
              fontSize: "var(--font-size-xs)",
              lineHeight: 1.6,
            }}
          >
            Actuals from the quarterly income statements (diluted EPS); consensus from the
            analyst-estimates snapshot.
          </div>
        </>
      )}

      {/* ── Data checks ── */}
      {data_checks.length > 0 && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-md)" }}>
          <strong>Data checks</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 20 }}>
            {data_checks.map((c, i) => (
              <li key={c.metric ?? i} style={{ margin: "4px 0" }}>
                <strong>{c.metric}</strong>
                {c.flag ? ` — ${String(c.flag).replace(/_/g, " ")}` : ""}
                {c.note ? `: ${c.note}` : ""}
              </li>
            ))}
          </ul>
        </Banner>
      )}

      {/* ── GAAP vs operational EPS ──
          Null when the quarter carried no material one-time gain, in which
          case the box is hidden entirely. */}
      {eps_normalization && (
        <Banner kind="warning" style={{ marginTop: "var(--spacing-md)" }}>
          <strong>
            The GAAP EPS surprise is inflated by a non-operating gain — not an operational
            blowout.
          </strong>
          <div style={{ marginTop: 8, fontWeight: 400, lineHeight: 1.7 }}>
            Consensus EPS is a clean/operational figure — analysts do not forecast one-time
            investment gains. Stripping the excess over normal other income puts operational EPS
            at <strong>{formatCurrency(eps_normalization.eps_operational)}</strong> against a
            reported <strong>{formatCurrency(eps_normalization.eps_gaap)}</strong>, turning a{" "}
            <strong>{fmtPct(eps_normalization.eps_surprise_pct_gaap, { decimals: 1 })}</strong>{" "}
            GAAP surprise into{" "}
            <strong>
              {fmtPct(eps_normalization.eps_surprise_pct_operational, { decimals: 1 })}
            </strong>{" "}
            operationally. Model on operating earnings — this line is volatile and can reverse.
          </div>

          <div
            style={{
              marginTop: 10,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(215px, 1fr))",
              gap: "2px 18px",
              fontSize: "var(--font-size-xs)",
              fontWeight: 400,
            }}
          >
            {eps_normalization.reported_other_income != null && (
              <div>
                <span style={{ opacity: 0.75 }}>Reported other income: </span>
                <strong>
                  {formatCurrency(eps_normalization.reported_other_income, { compact: true })}
                </strong>
              </div>
            )}
            {eps_normalization.normal_other_income != null && (
              <div>
                <span style={{ opacity: 0.75 }}>Normal run-rate: </span>
                <strong>
                  {formatCurrency(eps_normalization.normal_other_income, { compact: true })}
                </strong>
              </div>
            )}
            {eps_normalization.excess_pretax != null && (
              <div>
                <span style={{ opacity: 0.75 }}>Excess (pre-tax): </span>
                <strong>{formatCurrency(eps_normalization.excess_pretax, { compact: true })}</strong>
              </div>
            )}
            {eps_normalization.effective_tax_rate != null && (
              <div>
                <span style={{ opacity: 0.75 }}>Effective tax on it: </span>
                <strong>{fmtPct(eps_normalization.effective_tax_rate * 100, { signed: false, decimals: 2 })}</strong>
              </div>
            )}
            {eps_normalization.excess_after_tax_per_share != null && (
              <div>
                <span style={{ opacity: 0.75 }}>After-tax per share: </span>
                <strong>{formatCurrency(eps_normalization.excess_after_tax_per_share)}</strong>
              </div>
            )}
            {eps_normalization.share_of_eps != null && (
              <div>
                <span style={{ opacity: 0.75 }}>Share of reported EPS: </span>
                <strong>{fmtPct(eps_normalization.share_of_eps * 100, { signed: false, decimals: 1 })}</strong>
              </div>
            )}
          </div>

          {epsNormStatusNote(eps_normalization.status) && (
            <div style={{ marginTop: 8, fontWeight: 600 }}>
              {epsNormStatusNote(eps_normalization.status)}
            </div>
          )}
          {eps_normalization.method && (
            <div style={{ marginTop: 6, fontWeight: 400, opacity: 0.85, fontSize: "var(--font-size-xs)" }}>
              Method: {eps_normalization.method}
            </div>
          )}
        </Banner>
      )}

      {/* ── Key drivers vs thesis ── */}
      {key_drivers.length > 0 && (
        <>
          <SectionTitle
            caption={[
              latest_quarter?.fiscal_quarter != null || latest_quarter?.fiscal_year != null
                ? `from the ${fiscalLabel(
                    latest_quarter.fiscal_year,
                    latest_quarter.fiscal_quarter
                  )} earnings call`
                : "from the earnings call",
              sentiment?.transcript_date ? fmtDate(sentiment.transcript_date) : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            Key drivers vs thesis
          </SectionTitle>
          <NoteList items={key_drivers} boldKey="point" bodyKeys={["quote"]} />
        </>
      )}

      {/* ── Estimate & model implications ── */}
      {estimate_model_implications.length > 0 && (
        <>
          <SectionTitle caption="what the print does to the numbers">
            Estimate &amp; model implications
          </SectionTitle>
          <NoteList items={estimate_model_implications} />
        </>
      )}

      {/* ── Watch items / risks ── */}
      {watch_items.length > 0 && (
        <>
          <SectionTitle caption="what could go wrong from here">Watch items / risks</SectionTitle>
          <NoteList items={watch_items} boldKey="item" bodyKeys={["detail"]} />
        </>
      )}

      {/* ── Management sentiment ── */}
      {sentiment && (
        <>
          <SectionTitle
            caption={`model-derived from the ${fiscalLabel(
              sentiment.fiscal_year,
              sentiment.fiscal_quarter
            )} call transcript${
              sentiment.transcript_date ? ` · ${fmtDate(sentiment.transcript_date)}` : ""
            }`}
          >
            Management sentiment
          </SectionTitle>

          <div
            style={{
              display: "flex",
              gap: "var(--spacing-md)",
              flexWrap: "wrap",
              alignItems: "stretch",
            }}
          >
            <Card accent={band?.color} style={{ minWidth: 190, textAlign: "center" }}>
              <div
                style={{
                  color: "var(--color-text-muted)",
                  fontSize: "var(--font-size-xs)",
                  fontWeight: "var(--font-weight-semibold)",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                }}
              >
                Sentiment score
              </div>
              <div
                style={{
                  color: band?.color ?? "var(--color-text-primary)",
                  fontSize: "var(--font-size-4xl)",
                  fontWeight: "var(--font-weight-black)",
                  lineHeight: 1.1,
                  fontVariantNumeric: "tabular-nums",
                  marginTop: 4,
                }}
              >
                {fmtNum(sentiment.composite_score, 1)}
                <span style={{ fontSize: "var(--font-size-lg)", color: "var(--color-text-muted)" }}>
                  /100
                </span>
              </div>
              {band && (
                <div
                  style={{
                    color: band.color,
                    fontSize: "var(--font-size-sm)",
                    fontWeight: "var(--font-weight-bold)",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginTop: 2,
                  }}
                >
                  {sentiment.band ?? band.key}
                </div>
              )}
              {qoq != null && (
                <div
                  style={{
                    marginTop: 8,
                    color: toneColor(qoqTone),
                    fontSize: "var(--font-size-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {Number(qoq) >= 0 ? "▲" : "▼"} {fmtNum(Math.abs(Number(qoq)), 1)} QoQ
                </div>
              )}
            </Card>

            {dimensions.length > 0 && (
              <Card style={{ flex: 1, minWidth: 300 }}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 500,
                    marginBottom: 10,
                  }}
                >
                  Sub-scores (transcript-evidenced, 0–10). Hover a row for the prepared-vs-Q&amp;A
                  split and the supporting quote.
                </div>
                <DimensionBars dimensions={dimensions} />
              </Card>
            )}
          </div>

          {/* provenance line */}
          {provenance && (
            <div
              style={{
                marginTop: 6,
                color: "var(--color-text-muted)",
                fontSize: "var(--font-size-xs)",
                lineHeight: 1.6,
              }}
            >
              {provenance}
            </div>
          )}

          {sentiment.composite_score != null && (
            <BandStrip score={sentiment.composite_score} bandScale={sentiment.band_scale} />
          )}

          {/* trend — the trend matters more than the level; click a quarter for its reasons */}
          {(() => {
            const qKey = (h) => `${h.fiscal_year}-${h.fiscal_quarter}`;
            const detailed = qHist.length > 0;
            const trend = detailed ? qHist : sentiment_history;
            if (trend.length < (detailed ? 1 : 2)) return null;
            const selIdx = trend.findIndex((h) => qKey(h) === selQ);
            return (
              <div style={{ marginTop: "var(--spacing-md)" }}>
                <div
                  style={{
                    color: "var(--color-text-muted)",
                    fontSize: "var(--font-size-xs)",
                    fontWeight: 500,
                    marginBottom: 6,
                  }}
                >
                  Trend (newest first) — a falling composite on good prints is the classic
                  late-cycle tell.{detailed ? " Click a quarter to see why it scored that way." : ""}
                </div>
                <div style={{ display: "flex", gap: "var(--spacing-sm)", flexWrap: "wrap" }}>
                  {trend.map((h, i) => {
                    const hb = resolveBand(h.band, h.composite_score);
                    const isSel = detailed && qKey(h) === selQ;
                    const older = trend[i + 1];
                    const d =
                      h.composite_score != null && older?.composite_score != null
                        ? Number(h.composite_score) - Number(older.composite_score)
                        : null;
                    return (
                      <div
                        key={`${qKey(h)}-${i}`}
                        role={detailed ? "button" : undefined}
                        tabIndex={detailed ? 0 : undefined}
                        onClick={detailed ? () => setSelQ(isSel ? null : qKey(h)) : undefined}
                        onKeyDown={
                          detailed
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  setSelQ(isSel ? null : qKey(h));
                                }
                              }
                            : undefined
                        }
                        style={{
                          cursor: detailed ? "pointer" : "default",
                          borderRadius: "var(--radius-lg)",
                          outline: isSel ? `2px solid ${hb?.color ?? "var(--color-primary)"}` : "none",
                          outlineOffset: 1,
                        }}
                      >
                        <Card accent={hb?.color} style={{ padding: "8px 14px", minWidth: 118 }}>
                          <div
                            style={{
                              color: "var(--color-text-muted)",
                              fontSize: "var(--font-size-xs)",
                              fontWeight: "var(--font-weight-semibold)",
                            }}
                          >
                            {fiscalLabel(h.fiscal_year, h.fiscal_quarter)}
                          </div>
                          <div
                            style={{
                              color: hb?.color ?? "var(--color-text-primary)",
                              fontSize: "var(--font-size-lg)",
                              fontWeight: "var(--font-weight-bold)",
                              fontVariantNumeric: "tabular-nums",
                            }}
                          >
                            {fmtNum(h.composite_score, 1)}
                            {d != null ? (
                              <span
                                style={{
                                  fontSize: "var(--font-size-xs)",
                                  marginLeft: 6,
                                  color: d >= 0 ? "var(--color-success)" : "var(--color-error)",
                                }}
                              >
                                {signed1(d)}
                              </span>
                            ) : null}
                          </div>
                          <div style={{ color: "var(--color-text-muted)", fontSize: 10.5 }}>
                            {h.band}
                          </div>
                        </Card>
                      </div>
                    );
                  })}
                </div>
                {detailed && selIdx >= 0 && <QuarterDetail q={trend[selIdx]} prior={trend[selIdx + 1]} />}
              </div>
            );
          })()}

          {sentiment.summary && (
            <Card style={{ marginTop: "var(--spacing-md)" }}>
              <div
                style={{
                  color: "var(--color-text-secondary)",
                  fontSize: "var(--font-size-sm)",
                  lineHeight: 1.65,
                }}
              >
                {sentiment.summary}
              </div>
            </Card>
          )}

          {/* how to read this — the note's flag box, plus the required disclaimer */}
          <Banner kind="warning" style={{ marginTop: "var(--spacing-md)" }}>
            <strong>How to read this: </strong>
            the score is a transparent, transcript-evidenced <em>tone</em> read on a 0–100 scale. It
            is not a market-standard metric, and earnings-call tone is inherently promotional —
            management talks its book. {SENTIMENT_DISCLAIMER}
            {sentiment.composite_formula && (
              <div style={{ marginTop: 8, fontWeight: 400 }}>
                <strong>Composite: </strong>
                weighted 0–10 sub-scores → 0–100. {sentiment.composite_formula}
              </div>
            )}
          </Banner>

          {/* evidence table */}
          {withQuotes.length > 0 && (
            <>
              <SectionTitle caption="supporting transcript quotes">Evidence</SectionTitle>
              <Scroller>
                <Table>
                  <thead>
                    <tr>
                      <Th left>Dimension</Th>
                      <Th>Score</Th>
                      <Th left style={{ minWidth: 320 }}>Transcript evidence</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {withQuotes.map((d) => (
                      <tr key={d.key ?? d.label}>
                        <Tl>{d.label ?? d.key}</Tl>
                        <Td bold style={{ color: subScoreColor(d.score) }}>
                          {fmtNum(d.score, 1)}
                        </Td>
                        <Td left wrap tone="muted" style={{ fontStyle: "italic" }}>
                          “{d.quote}”
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Scroller>
            </>
          )}
        </>
      )}

      {/* ── Guidance ── */}
      {/* ── How the management-sentiment score is built (methodology, 2026-10-09) ── */}
      {sentiment && (
        <details style={{ marginTop: "var(--spacing-md)" }}>
          <summary style={{ cursor: "pointer", color: "var(--color-text-primary)", fontWeight: 600, fontSize: "var(--font-size-md, 15px)" }}>
            How the management-sentiment score is built
          </summary>
          <div style={{ color: "var(--color-text-secondary)", fontSize: "var(--font-size-sm)", lineHeight: 1.65, marginTop: "var(--spacing-sm)" }}>
            <p style={{ margin: "0 0 8px" }}>
              <strong>1. The call is split in two</strong> — prepared remarks and the Q&amp;A — and each half is scored
              separately on five dimensions, 0–10 (5 = neutral: routine, non-committal or not discussed). Every score must be
              backed by a quote copied word for word from that half of the transcript; a quote that cannot be found
              there is flagged as unverified. 7+ needs a specific, favourable, quantified statement; 3 or below a specific,
              unfavourable one; 0–2 and 8–10 need unambiguous language. Length and enthusiasm earn nothing.
            </p>
            <p style={{ margin: "0 0 6px" }}>
              <strong>2. The two halves are blended 40% prepared / 60% Q&amp;A</strong> — the Q&amp;A is unscripted, so it
              counts for more. A dimension evidenced in only one half takes that half's score; one discussed in neither is
              left out of the composite rather than counted as 5.
            </p>
            <Scroller>
              <Table>
                <thead>
                  <tr><Th left>Dimension</Th><Th>Weight</Th><Th left>What moves it</Th></tr>
                </thead>
                <tbody>
                  <tr><Tl>Demand &amp; Backlog</Tl><Td>25%</Td><Td left wrap>orders, backlog, volumes, customer demand, supply vs demand</Td></tr>
                  <tr><Tl>Guidance Posture</Tl><Td>25%</Td><Td left wrap>the stance on the outlook: withdrawn 0–1 · cut 2–3 · hedged 4 · reaffirmed 5 · reaffirmed with detail 6 · raised at the low end 7 · midpoint raised 8 · raised materially with volunteered detail 9–10 — the stance, not whether the numbers are good</Td></tr>
                  <tr><Tl>Pricing &amp; Margin</Tl><Td>20%</Td><Td left wrap>pricing power, gross/operating margin, cost pressure</Td></tr>
                  <tr><Tl>Candor / No Dodging</Tl><Td>20%</Td><Td left wrap>direct answers to direct questions; <strong>capped at 7</strong> when management dodged a question (the dodge is recorded)</Td></tr>
                  <tr><Tl>Capital Return</Tl><Td>10%</Td><Td left wrap>buybacks, dividends, capex and investment plans</Td></tr>
                </tbody>
              </Table>
            </Scroller>
            <p style={{ margin: "10px 0 4px" }}>
              <strong>3. Composite</strong> = Σ(weight × dimension score) ÷ Σ(weight) × 10, over the dimensions the call
              evidenced, on a 0–100 scale. The change vs the previous quarter is shown only when both calls were scored under
              the same rubric version.
            </p>
            <p style={{ margin: 0 }}>
              <strong>Bands:</strong> 85+ Very Bullish / High Conviction · 70+ Bullish / Confident · 55+ Constructive ·
              45+ Neutral / Mixed · 30+ Cautious · below 30 Defensive / Negative. It scores management's tone, not the
              results, and is never averaged with the weekly news score.
            </p>
          </div>
        </details>
      )}

      {(guidanceRows.length > 0 || guidance?.summary) && (
        <>
          <SectionTitle
            caption={[
              guidancePeriod ? `${guidancePeriod} guidance` : null,
              guidance?.source ? `source: ${guidance.source}` : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          >
            Management guidance
          </SectionTitle>

          {guidanceRows.length > 0 && (
            <Scroller>
              <Table>
                <thead>
                  <tr>
                    <Th left>Metric</Th>
                    <Th>Guided</Th>
                    <Th left>Range</Th>
                    <Th>vs the quarter just reported</Th>
                  </tr>
                </thead>
                <tbody>
                  {guidanceRows.map((r) => {
                    const rowTint = r.excluded ? { backgroundColor: "rgba(217,119,6,0.08)" } : undefined;
                    return (
                      <tr key={r.label}>
                        <Tl style={rowTint}>
                          {r.label}
                          {r.excluded && (
                            <MuiTooltip
                              title="Management explicitly excluded this from the outlook — the guide is not comparable with a figure that includes it."
                              placement="top"
                              arrow
                            >
                              <span style={{ color: "var(--color-warning)", cursor: "help" }}> ⚠</span>
                            </MuiTooltip>
                          )}
                        </Tl>
                        {/* Qualitative guides are management's own words — left-align
                            and wrap them rather than forcing a numeric column. */}
                        <Td
                          bold
                          left={!!r.verbatim}
                          wrap={!!r.verbatim}
                          tone={r.excluded ? "warn" : undefined}
                          style={rowTint}
                        >
                          {r.verbatim ? `“${r.guided}”` : r.guided}
                        </Td>
                        <Td left tone="muted" style={rowTint}>{r.range ?? "—"}</Td>
                        <Td bold tone={r.vsTone} style={rowTint}>{r.vsActual ?? "—"}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Scroller>
          )}

          {guidance?.summary && (
            <Card style={{ marginTop: "var(--spacing-sm)" }}>
              <div
                style={{
                  color: "var(--color-text-secondary)",
                  fontSize: "var(--font-size-sm)",
                  lineHeight: 1.65,
                }}
              >
                {guidance.summary}
              </div>
            </Card>
          )}
        </>
      )}

      {/* ── Beat/miss history ── */}
      {quarters.length > 0 && (
        <>
          <SectionTitle caption="newest first · beat/miss from the surprise sign · diluted EPS">
            Beat / miss history
          </SectionTitle>
          <Scroller>
            <Table>
              <thead>
                <tr>
                  <Th left>Period</Th>
                  <Th left>Quarter ended</Th>
                  <Th>EPS est.</Th>
                  <Th>EPS actual</Th>
                  <Th>Surprise</Th>
                  <Th>Revenue est.</Th>
                  <Th>Revenue actual</Th>
                  <Th>Surprise</Th>
                </tr>
              </thead>
              <tbody>
                {quarters.map((q, i) => {
                  const reported = q.eps_actual != null || q.revenue_actual != null;
                  return (
                    <tr key={`${q.fiscal_year}-${q.fiscal_quarter}-${i}`}>
                      <Tl>
                        {fiscalLabel(q.fiscal_year, q.fiscal_quarter)}
                        {!reported && (
                          <span style={{ marginLeft: 8 }}>
                            <Pill>Not yet reported</Pill>
                          </span>
                        )}
                      </Tl>
                      <Td left tone="muted">
                        {fmtDate(q.period_end_date ?? q.report_date)}
                      </Td>
                      <Td>{q.eps_estimate != null ? formatCurrency(q.eps_estimate) : "—"}</Td>
                      <Td bold>{q.eps_actual != null ? formatCurrency(q.eps_actual) : "—"}</Td>
                      <Td bold tone={surpriseTone(q.eps_surprise_pct)}>
                        {fmtPct(q.eps_surprise_pct)}
                      </Td>
                      <Td>
                        {q.revenue_estimate != null
                          ? formatCurrency(q.revenue_estimate, { compact: true })
                          : "—"}
                      </Td>
                      <Td bold>
                        {q.revenue_actual != null
                          ? formatCurrency(q.revenue_actual, { compact: true })
                          : "—"}
                      </Td>
                      <Td bold tone={surpriseTone(q.revenue_surprise_pct)}>
                        {fmtPct(q.revenue_surprise_pct)}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Scroller>
          {quarters[0]?.source && (
            <div
              style={{
                marginTop: 6,
                color: "var(--color-text-muted)",
                fontSize: "var(--font-size-xs)",
              }}
            >
              Source: {quarters[0].source}.
            </div>
          )}
        </>
      )}

      {/* ── Next report ── */}
      {nextTiles.length > 0 && (
        <>
          <SectionTitle>Next report</SectionTitle>
          <TileGrid tiles={nextTiles} />
        </>
      )}

      {/* No sentiment coverage */}
      {!sentiment && (
        <Banner kind="plain" style={{ marginTop: "var(--spacing-md)" }}>
          No management-sentiment score is available for {ticker}. The sentiment engine covers the
          S&amp;P 500 universe on a rolling four-quarter window, refreshed Saturdays.
        </Banner>
      )}

      {/* ── Footer ── */}
      <div
        style={{
          marginTop: "var(--spacing-xl)",
          paddingTop: "var(--spacing-md)",
          borderTop: "1px solid var(--color-border-primary)",
          color: "var(--color-text-muted)",
          fontSize: "var(--font-size-xs)",
          lineHeight: 1.6,
        }}
      >
        Actuals and consensus come from the quarterly statements and the analyst-estimates
        snapshot; the sentiment score is model-derived from the earnings-call transcript, and the
        key drivers, estimate implications and watch items are written from that same call.
        Sentiment and guidance refresh Saturdays 12:00 ET. For internal analysis only — not
        investment advice.
      </div>
    </NotePage>
  );
};

export default EarningsReview;
